import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components';

const EmptyDot = (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill="#D9D9D9"/>
  </svg>
);

const ActiveDot = (
	<svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill="#3393EC"/>
  </svg>
);

const SliderWrapper = styled.div`
	width: 100%;
	height: 100%;
	position: relative;
	overflow: hidden;
`;

const SliderList = styled.ul`
	display: flex;
	width: 100%;
	height: 100%;
	list-style: none;
	padding: 0;
	margin: 0;
`;

const ItemSlide = styled.li`
	flex-shrink: 0;
	width: 100%;
	height: 100%;
`;

const DotsContainer = styled.div`
	display: flex;
	justify-content: center;
`;

const CommentItem = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 350px;
  margin: 30px auto;
`

const FeatureImg = styled.div`
  background-image: ${({ imgPath }) => imgPath && `url('${imgPath}')`};
	background-position: top;
	background-size: 130% 70%;
  background-repeat: no-repeat;
  display: flex;
  max-width: 290px;
  max-height: 320px;
  min-height: 350px;
  height: 100%;
  width: 100%;
  position: relative;
`

const Title = styled.h3`
  text-align: center;
  margin-top: -90px;
	font-weight: ${(props) => props.theme.fontWeights.medium};
	font-size: ${(props) => props.theme.fontSizes.font24};
	color: ${(props) => props.theme.colors.colorText};
  letter-spacing: -0.12px;
  position: relative;
  z-index: 100;
`

const Description = styled.p`
  color: ${(props) => props.theme.colors.colorText};
  text-align: center;
  font-size: ${(props) => props.theme.fontSizes.font16};
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  letter-spacing: -0.461px;
  margin-top: 8px;
  position: relative;
  z-index: 100;
`

const Dot = styled.div`
	&:not(:last-child) {
		margin-right: 5px;
	}
`;

const FeaturesSlider = ({ slideData }) => {
	const containerRef = useRef(null);
	const intervalRef = useRef(null);
	const [current, setCurrent] = useState(1);
	const [translateX, setTranslateX] = useState(0);
	const handleTouchStart = (e) => {
		const startX = e.touches[0].clientX;
		let deltaX = 0;

		const handleTouchMove = (e) => {
			deltaX = e.touches[0].clientX - startX;
		};

		const handleTouchEnd = () => {
			if (deltaX !== 0) {
				const sensitivity = 50;
				if (deltaX > sensitivity) {
					handleSwipe('prev');
				} else if (deltaX < -sensitivity) {
					handleSwipe('next');
				}
			}

			deltaX = 0;
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', handleTouchEnd);
		};

		document.addEventListener('touchmove', handleTouchMove);
		document.addEventListener('touchend', handleTouchEnd);
	};

	const SlideItem = ({ id, imgPath, title, description }) => {
		return (
			<ItemSlide>
				<CommentItem key={id}>
          <FeatureImg imgPath={imgPath}/>
          <Title>{title}</Title>
          <Description>{description}</Description>
        </CommentItem>
			</ItemSlide>
		);
	};

	const slides = useMemo(() => {
		if (slideData.length > 1) {
			let items = slideData.map((data, index) => (
				<SlideItem
					key={data.id}
					imgPath={data.imgPath}
					title={data.title}
					description={data.description}
				/>
			));
			return [
				<SlideItem
					key={slideData.length + 1}
					imgPath={slideData[slideData.length - 1].imgPath}
					title={slideData[slideData.length - 1].title}
					description={slideData[slideData.length - 1].description}
				></SlideItem>,
				...items,
				<SlideItem
					key={slideData.length + 2}
					imgPath={slideData[0].imgPath}
					title={slideData[0].title}
					description={slideData[0].description}
				></SlideItem>,
			];
		}
		return (
			<SlideItem
				key={slideData.length + 2}
				imgPath={slideData[0].imgPath}
				title={slideData[0].title}
				description={slideData[0].description}
			></SlideItem>
		);
	}, [slideData]);

	const handleSwipe = useCallback(
		(mode) => {
			containerRef.current.style.transitionDuration = '400ms';
			if (mode === 'prev') {
				if (current <= 1) {
					setTranslateX(0);
					setCurrent(slideData.length);
				} else {
					setTranslateX(containerRef.current.clientWidth * (current - 1));
					setCurrent((prev) => --prev);
				}
			} else if (mode === 'next') {
				if (current >= slideData.length) {
					setTranslateX(
						containerRef.current.clientWidth * (slideData.length + 1)
					);
					setCurrent(1);
				} else {
					setTranslateX(containerRef.current.clientWidth * (current + 1));
					setCurrent((prev) => ++prev);
				}
			}
		},
		[current, slideData]
	);

	useEffect(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => {
			handleSwipe('next');
		}, 5000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [handleSwipe]);

	useLayoutEffect(() => {
		setTranslateX(containerRef.current.clientWidth * current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const transitionEnd = () => {
			if (current <= 1) {
				containerRef.current.style.transitionDuration = '0ms';
				setTranslateX(containerRef.current.clientWidth * current);
			}
			if (current >= slideData.length) {
				containerRef.current.style.transitionDuration = '0ms';
				setTranslateX(containerRef.current.clientWidth * slideData.length);
			}
		};
		document.addEventListener('transitionend', transitionEnd);
		return () => {
			document.removeEventListener('transitionend', transitionEnd);
		};
	}, [current, slideData]);
	return (
		<>
			<SliderWrapper>
				<SliderList
					ref={containerRef}
					style={{
						transform: `translate3d(${-translateX}px, 0, 0)`,
					}}
					onTouchStart={handleTouchStart}
				>
					{slides}
				</SliderList>
			</SliderWrapper>
			<DotsContainer>
				{slides.map((_, slideIndex) => {
					if (slideIndex > 0 && slideIndex < slides.length - 1) {
						return (
							<Dot key={slideIndex}>
								{slideIndex === current ? ActiveDot : EmptyDot}
							</Dot>
						);
					} else return null;
				})}
			</DotsContainer>
		</>
	);
};

export default FeaturesSlider;
